import React, { useCallback, useState } from 'react';

import { App, Button, Form, Input, Modal, Switch, Typography } from 'antd';

import { useEditProductCampaign, useCreateProductCampaign } from 'v2/hooks/campaigns';
import { useUserStore } from 'v2/store/User';

import Coaffiliates from './components/Coaffiliates';

interface IProps {
  campaign?: any;
  product: any;
  handleClose: any;
  onChangeTab?: any;
  producer?: any;
  tabActive?: any;
  updateListCampaigns?: any;
  open: any;
}

const CampaignForm: React.FC<IProps> = ({ campaign: campaignProp, product, handleClose, open }) => {
  const { message } = App.useApp();
  const { user } = useUserStore(({ user }) => ({ user }));

  const [isReadyToSave, setIsReadyToSave] = useState(true);
  const [isEditing] = useState(!!campaignProp);
  const [campaign, setCampaign] = useState<any>(() => {
    if (campaignProp) {
      const campaignCopy = JSON.parse(JSON.stringify(campaignProp));
      campaignCopy.product_id = product.id;
      return campaignCopy;
    }

    return {
      active: true,
      title: '',
      product_id: product.id,
      url: '',
      public: false,
      coaffiliates: [
        {
          customer_id: user.id,
          percent: 100,
          name: user.full_name,
          email: user.email
        }
      ]
    };
  });

  const editMutation = useEditProductCampaign();
  const createMutation = useCreateProductCampaign();

  const isBusy = editMutation.isPending || createMutation.isPending;

  const handleChange = useCallback((field: any, e: any) => {
    setCampaign((prev: any) => ({ ...prev, [field]: e.target.value }));
  }, []);

  const handleChangeCheck = useCallback((field: any, e: any) => {
    setCampaign((prev: any) => ({ ...prev, [field]: e.target.checked }));
  }, []);

  const handleChangeCoAffiliates = useCallback((coaffiliates: any) => {
    setCampaign((prev: any) => ({ ...prev, coaffiliates }));
  }, []);

  const handleSubmit = useCallback(async () => {
    let campaignData = { ...campaign };

    if (campaignData.url === '') {
      message.error('A URL não pode estar vazia');
      return;
    }

    if (campaignData.title === '') {
      message.error('O nome da campanha não pode estar vazio');
      return;
    }

    if (!product.is_producer) {
      if (campaignData.coaffiliates.filter((item: any) => item.percent === 0).length > 0) {
        message.error('Nenhum coafiliado deve ter valor zero');
        return;
      }

      if (campaignData.coaffiliates.filter((item: any) => item.customer_id === 0).length > 0) {
        message.error('Adicione coafiliados válidos');
        return;
      }

      if (
        campaignData.coaffiliates.reduce((acc: number, item: any) => acc + Number.parseFloat(item.percent), 0) !== 100
      ) {
        message.error('A soma das porcentagens de coafiliados deve ser 100');
        return;
      }
    } else {
      delete campaignData.coaffiliates;
    }

    campaignData = { ...campaignData, url: campaignData.url.trim() };

    if (isEditing) {
      try {
        delete campaignData.status;
        await editMutation.mutateAsync({ productId: product.id, campaignId: campaignData.id, data: campaignData });
        message.success('Campanha alterada com sucesso!');
        handleClose(true);
      } catch (error) {
        message.error(error instanceof Error ? error.message : 'Não foi possível alterar a campanha');
      }
    } else {
      try {
        await createMutation.mutateAsync({ productId: product.id, campaign: campaignData });
        message.success('Campanha criada com sucesso!');
        handleClose();
      } catch (error) {
        message.error(error instanceof Error ? error.message : 'Não foi possível criar a campanha');
      }
    }
  }, [campaign, product, isEditing, handleClose, editMutation, createMutation, message]);

  return (
    <Modal
      open={open}
      onCancel={handleClose}
      title={
        <div>
          <div>{isEditing ? 'Edição de campanha' : 'Cadastro de campanha'}</div>
          <Typography.Text type='secondary' className='text-sm font-normal'>
            {isEditing ? 'Edite seu funil/campanha' : 'Crie seu funil/campanha'}
          </Typography.Text>
        </div>
      }
      width={720}
      footer={
        <div className='flex flex-wrap items-center justify-end gap-2'>
          <Button type='text' id='campaign-form-close' disabled={isBusy} onClick={handleClose}>
            Fechar
          </Button>
          <Button
            id='campaign-form-save'
            type='primary'
            disabled={isBusy || !isReadyToSave}
            onClick={handleSubmit}
            loading={isBusy}
          >
            Salvar
          </Button>
        </div>
      }
    >
      <Form layout='vertical' className='flex flex-col gap-1'>
        <div className='mb-2 flex items-center gap-2'>
          <Switch
            size='small'
            checked={campaign.active}
            onChange={(checked: boolean) => handleChangeCheck('active', { target: { checked } })}
          />
          <Typography.Text>{campaign.active ? 'Ativo' : 'Inativo'}</Typography.Text>
        </div>

        <Form.Item label='Nome da Campanha' extra='Ex: Campanha de lançamento' className='!mb-3'>
          <Input
            id='campaign-form-title'
            size='small'
            value={campaign.title}
            onChange={(e: any) => handleChange('title', e)}
          />
        </Form.Item>

        <Form.Item label='URL de destino' extra='Ex: https://google.com' className='!mb-3'>
          <Input
            id='campaign-form-url'
            size='small'
            value={campaign.url}
            onChange={(e: any) => handleChange('url', e)}
          />
        </Form.Item>

        {!product.is_producer && (
          <div className='flex flex-col gap-3'>
            <Typography.Title level={5} className='!mb-0'>
              Coafiliados
            </Typography.Title>
            <Coaffiliates
              coaffiliates={campaign.coaffiliates}
              handleChangeCoAffiliates={handleChangeCoAffiliates}
              public={campaign.public}
              onChangeCheck={handleChangeCheck}
              setReadyToSave={setIsReadyToSave}
            />
          </div>
        )}
      </Form>
    </Modal>
  );
};

export default CampaignForm;
